"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useTranslations } from "next-intl"
import { IconSpy } from "@tabler/icons-react"

import { Button } from "@/components/ui/button"
import { useAuthStore } from "@/stores/auth-store"

/**
 * Persistent warning bar shown while an admin is acting as another user.
 * Deliberately not dismissible (unlike the push-notification banner): losing
 * track of an impersonated session is exactly the failure mode this prevents.
 */
export function ImpersonationBanner() {
  const t = useTranslations("impersonation")
  const router = useRouter()
  const isImpersonating = useAuthStore((s) => s.isImpersonating)
  const user = useAuthStore((s) => s.user)
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation)
  const [isStopping, setIsStopping] = useState(false)

  if (!isImpersonating) {
    return null
  }

  const handleStop = async () => {
    setIsStopping(true)
    try {
      await stopImpersonation()
      router.push("/")
    } finally {
      setIsStopping(false)
    }
  }

  return (
    <div className="flex items-center gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 lg:px-6">
      <IconSpy className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
      <p className="flex-1 text-sm font-medium text-amber-900 dark:text-amber-200">
        {t("banner", { name: user?.full_name ?? "" })}
      </p>
      <Button
        size="sm"
        variant="outline"
        className="h-7 shrink-0 text-xs"
        onClick={handleStop}
        disabled={isStopping}
      >
        {t("stop")}
      </Button>
    </div>
  )
}
